import { motion } from "framer-motion";
import { useState } from "react";
import { ShoppingBag, MapPin, Mail, Phone, CheckCircle, AlertCircle, Truck, ExternalLink, Banknote, Smartphone, CreditCard } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const BOOK_PRICE = 15.99;
const PAYPAL_URL = "https://www.paypal.com/ncp/payment/BRGHYT9K5USCG";

const SHIPPING_OPTIONS = [
  { id: "pickup", label: "Barbados Pickup", price: 0, description: "Free pickup from designated location", eta: "Ready in 1–2 days" },
  { id: "delivery", label: "Barbados Delivery", price: 2, description: "Standard delivery within Barbados", eta: "Estimated delivery: 2–3 days" },
  { id: "rural", label: "Barbados Rural Delivery", price: 5, description: "St. Andrew, St. Joseph, St. John, St. Thomas, St. Lucy, and any location beyond Six Roads in St. Philip", eta: "Estimated delivery: 3–5 days" }
];

const PAYMENT_METHODS = [
  { id: "paypal", label: "PayPal", icon: CreditCard, description: "Pay securely online via PayPal" },
  { id: "cash_pickup", label: "Cash on Pickup", icon: Banknote, description: "Pay in cash when you collect your books" },
  { id: "bimpay", label: "BimPay", icon: Smartphone, description: "Mobile wallet — we'll send instructions" },
  { id: "firstpay", label: "FirstPay", icon: Smartphone, description: "CIBC Caribbean mobile pay — we'll send instructions" }
];

const BarbadosOrderPage = () => {
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    phone: "",
    address: "",
    delivery_option: "pickup",
    payment_method: "paypal",
    special_instructions: ""
  });

  const selectedShipping = SHIPPING_OPTIONS.find((o) => o.id === formData.delivery_option);
  const shippingCost = selectedShipping?.price || 0;
  const subtotal = BOOK_PRICE * quantity;
  const total = subtotal + shippingCost;
  const selectedPayment = PAYMENT_METHODS.find((p) => p.id === formData.payment_method);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/api/orders`, {
        customer_name: formData.customer_name,
        email: formData.email,
        phone: formData.phone,
        address_line1: formData.address || "Pickup",
        address_line2: "",
        city: "Barbados",
        state_province: formData.delivery_option === "pickup" ? "Pickup Location" : "Delivery",
        postal_code: "BB",
        country: "Barbados",
        quantity,
        product: "detective_diaries",
        shipping_override: shippingCost,
        order_source: "barbados",
        delivery_method: formData.delivery_option,
        payment_method: formData.payment_method,
        notes: `Delivery: ${selectedShipping?.label} | Payment: ${selectedPayment?.label}${formData.special_instructions ? `\nSpecial Instructions: ${formData.special_instructions}` : ""}`
      });
      setOrderNumber(data.order_number);
      setSubmitted(true);
      if (formData.payment_method === "paypal") {
        // Open PayPal in new tab so user keeps the confirmation page
        window.open(PAYPAL_URL, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-12 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="tactile-card p-8 md:p-12 text-center"
            data-testid="order-confirmation"
          >
            <div className="w-24 h-24 bg-[#2A9D8F] rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-[#1D3557]">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl font-black text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Order placed!
            </h1>
            <p className="text-lg text-[#1D3557] font-bold mb-1">{formData.customer_name.split(" ")[0]}, thanks for supporting Callie 🐄</p>
            <p className="text-[#1D3557]/70 font-medium font-mono mb-6">Order #{orderNumber}</p>

            <div className="bg-[#E9C46A]/20 rounded-2xl p-6 mb-6 text-left">
              <h3 className="font-bold text-[#1D3557] mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>Order summary</h3>
              <div className="space-y-2 text-[#1D3557] font-medium">
                <p>📚 {quantity} × <em>The Detective Diaries of Marchfield Meadows</em></p>
                <p>🚚 {selectedShipping?.label} — ${shippingCost.toFixed(2)}</p>
                <p>💳 Payment: <strong>{selectedPayment?.label}</strong></p>
                <p className="pt-2 border-t-2 border-[#1D3557]/10">Total: <strong className="text-[#E63946]">${total.toFixed(2)} USD</strong></p>
              </div>
            </div>

            {formData.payment_method === "paypal" && (
              <div className="bg-[#457B9D]/10 rounded-2xl p-6 mb-4 text-left">
                <h3 className="font-bold text-[#1D3557] mb-2 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  <CreditCard className="w-5 h-5 text-[#457B9D]" /> Pay with PayPal
                </h3>
                <p className="text-[#1D3557]/90 font-medium mb-3">PayPal opened in a new tab. If it didn't, tap below:</p>
                <a
                  href={PAYPAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="paypal-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open PayPal
                </a>
              </div>
            )}

            {formData.payment_method === "cash_pickup" && (
              <div className="bg-[#2A9D8F]/10 rounded-2xl p-6 mb-4 text-left">
                <h3 className="font-bold text-[#1D3557] mb-2 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  <Banknote className="w-5 h-5 text-[#2A9D8F]" /> Cash on Pickup
                </h3>
                <p className="text-[#1D3557]/90 font-medium">
                  Bring <strong>${total.toFixed(2)} USD</strong> (or BBD equivalent) when you collect your books. We'll email you the pickup details.
                </p>
              </div>
            )}

            {(formData.payment_method === "bimpay" || formData.payment_method === "firstpay") && (
              <div className="bg-[#E9C46A]/20 rounded-2xl p-6 mb-4 text-left">
                <h3 className="font-bold text-[#1D3557] mb-2 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  <Smartphone className="w-5 h-5 text-[#E63946]" /> {selectedPayment?.label}
                </h3>
                <p className="text-[#1D3557]/90 font-medium">
                  We'll email <strong>{formData.email}</strong> the {selectedPayment?.label} payment details shortly.
                </p>
              </div>
            )}

            <div className="bg-[#1D3557]/5 rounded-2xl p-6 text-left">
              <h3 className="font-bold text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>What's next?</h3>
              <ol className="list-decimal list-inside space-y-2 text-[#1D3557]/90 font-medium">
                <li>You can <a href={`/order/status?order=${orderNumber}`} className="text-[#E63946] font-bold underline">track your order</a> any time from the homepage footer</li>
                <li>Questions? Email <a href={`mailto:info@farmsmartkids.com?subject=Order%20${orderNumber}`} className="text-[#E63946] font-bold underline">info@farmsmartkids.com</a></li>
                <li>We'll be in touch to arrange {formData.delivery_option === "pickup" ? "pickup" : "delivery"}</li>
              </ol>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="text-5xl mb-4">🇧🇧</div>
          <div className="inline-block px-4 py-1.5 rounded-full bg-[#2A9D8F] text-white font-bold text-sm mb-4 uppercase tracking-wider">
            Now Available in Barbados
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Order Now
          </h1>
          <p className="text-lg text-[#1D3557] font-medium max-w-2xl mx-auto">
            Local stock is here! Pickup, delivery, and rural delivery available across Barbados — pay your way.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Book selection */}
              <div className="tactile-card p-6">
                <h2 className="text-xl font-bold text-[#1D3557] mb-6 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  <ShoppingBag className="w-6 h-6 text-[#E63946]" />
                  How many copies?
                </h2>
                <div className="flex flex-col sm:flex-row items-center gap-6 p-4 bg-[#E9C46A]/10 rounded-2xl border-2 border-[#1D3557]/10">
                  <img src="https://customer-assets.emergentagent.com/job_farm-tales-store/artifacts/d0h3ch73_image.png" alt="The Detective Diaries" className="w-24 h-24 rounded-xl object-cover border-2 border-[#1D3557]" />
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="text-lg font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                      The Detective Diaries of Marchfield Meadows
                    </h3>
                    <p className="text-[#1D3557]/70 font-medium text-sm leading-relaxed">
                      Farm adventures, mysteries, and hands-on learning inspired by Caribbean agriculture and the animals that make farm life unforgettable 🐄📚
                    </p>
                    <p className="text-xl font-bold text-[#E63946] mt-1">${BOOK_PRICE.toFixed(2)} USD</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 rounded-xl bg-white border-3 border-[#1D3557] flex items-center justify-center hover:bg-[#E9C46A]/20" data-testid="decrease-qty" aria-label="Decrease">−</button>
                    <span className="text-2xl font-bold text-[#1D3557] w-12 text-center" data-testid="quantity-display">{quantity}</span>
                    <button type="button" onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 rounded-xl bg-white border-3 border-[#1D3557] flex items-center justify-center hover:bg-[#E9C46A]/20" data-testid="increase-qty" aria-label="Increase">+</button>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="tactile-card p-6">
                <h2 className="text-xl font-bold text-[#1D3557] mb-6 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  <Mail className="w-6 h-6 text-[#2A9D8F]" />
                  Contact info
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-[#1D3557] font-bold mb-2">Full Name *</label>
                    <input type="text" name="customer_name" value={formData.customer_name} onChange={handleInputChange} className="input-field" required data-testid="customer-name-input" />
                  </div>
                  <div>
                    <label className="block text-[#1D3557] font-bold mb-2">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="input-field" required data-testid="email-input" />
                  </div>
                  <div>
                    <label className="block text-[#1D3557] font-bold mb-2"><Phone className="w-4 h-4 inline" /> Phone *</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="input-field" required data-testid="phone-input" />
                  </div>
                </div>
              </div>

              {/* Delivery */}
              <div className="tactile-card p-6">
                <h2 className="text-xl font-bold text-[#1D3557] mb-6 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  <Truck className="w-6 h-6 text-[#457B9D]" />
                  Delivery option
                </h2>
                <div className="space-y-3">
                  {SHIPPING_OPTIONS.map((option) => (
                    <label key={option.id} className={`block p-4 rounded-xl border-3 cursor-pointer transition-all ${formData.delivery_option === option.id ? "border-[#E63946] bg-[#E63946]/5" : "border-[#1D3557]/15 hover:border-[#1D3557]/40"}`} data-testid={`delivery-${option.id}`}>
                      <div className="flex items-start gap-3">
                        <input type="radio" name="delivery_option" value={option.id} checked={formData.delivery_option === option.id} onChange={handleInputChange} className="mt-1" />
                        <div className="flex-1">
                          <div className="flex flex-wrap justify-between gap-2">
                            <p className="font-bold text-[#1D3557]">{option.label}</p>
                            <p className="font-bold text-[#E63946]">{option.price === 0 ? "FREE" : `+ $${option.price.toFixed(2)}`}</p>
                          </div>
                          <p className="text-sm text-[#1D3557]/70 font-medium">{option.description}</p>
                          <p className="text-xs text-[#2A9D8F] font-bold mt-1 flex items-center gap-1">
                            <Truck className="w-3 h-3" />
                            {option.eta}
                          </p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                {formData.delivery_option !== "pickup" && (
                  <div className="mt-4">
                    <label className="block text-[#1D3557] font-bold mb-2">
                      <MapPin className="w-4 h-4 inline" /> Delivery address *
                    </label>
                    <textarea name="address" value={formData.address} onChange={handleInputChange} className="input-field" rows="3" required placeholder="Street, area, parish — anything that helps us find you" data-testid="address-input" />
                  </div>
                )}
              </div>

              {/* Payment method */}
              <div className="tactile-card p-6">
                <h2 className="text-xl font-bold text-[#1D3557] mb-6 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  <CreditCard className="w-6 h-6 text-[#E63946]" />
                  How would you like to pay?
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PAYMENT_METHODS.map((pm) => {
                    const Icon = pm.icon;
                    return (
                      <label key={pm.id} className={`block p-4 rounded-xl border-3 cursor-pointer transition-all ${formData.payment_method === pm.id ? "border-[#E63946] bg-[#E63946]/5" : "border-[#1D3557]/15 hover:border-[#1D3557]/40"}`} data-testid={`payment-${pm.id}`}>
                        <div className="flex items-start gap-3">
                          <input type="radio" name="payment_method" value={pm.id} checked={formData.payment_method === pm.id} onChange={handleInputChange} className="mt-1" />
                          <Icon className="w-5 h-5 text-[#1D3557] flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-bold text-[#1D3557]">{pm.label}</p>
                            <p className="text-xs text-[#1D3557]/70 font-medium">{pm.description}</p>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div className="tactile-card p-6">
                <label className="block text-[#1D3557] font-bold mb-2">Special instructions (optional)</label>
                <textarea name="special_instructions" value={formData.special_instructions} onChange={handleInputChange} className="input-field" rows="3" placeholder="Anything we should know?" data-testid="notes-input" />
              </div>

              {error && (
                <div className="bg-[#E63946]/10 border-4 border-[#E63946] rounded-2xl p-4 flex items-center gap-3" data-testid="order-error">
                  <AlertCircle className="w-6 h-6 text-[#E63946] flex-shrink-0" />
                  <p className="text-[#E63946] font-medium">{error}</p>
                </div>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full text-xl py-4 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="place-order-btn">
                <ShoppingBag className="w-5 h-5" />
                {loading ? "Placing order…" : `Place order — $${total.toFixed(2)}`}
              </button>
            </form>
          </motion.div>

          {/* Summary */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}>
            <div className="tactile-card p-6 sticky top-24">
              <h2 className="text-xl font-bold text-[#1D3557] mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>Order Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-[#1D3557] font-medium">
                  <span>{quantity} × Book</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#1D3557] font-medium">
                  <span>{selectedShipping?.label}</span>
                  <span>{shippingCost === 0 ? "FREE" : `$${shippingCost.toFixed(2)}`}</span>
                </div>
                <div className="border-t-4 border-[#1D3557]/10 pt-3 flex justify-between text-[#1D3557] font-bold text-lg">
                  <span>Total</span>
                  <span className="text-[#E63946]">${total.toFixed(2)} USD</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-[#2A9D8F]/10 rounded-xl border-2 border-[#2A9D8F]/30">
                <p className="text-sm text-[#1D3557] font-bold mb-1">🎁 Reservers — your priority slot is honored!</p>
                <p className="text-sm text-[#1D3557] font-medium">Use the email you reserved with and we'll match you up.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BarbadosOrderPage;
